import React, { useState } from "react";
import PropTypes from "prop-types";
import { Group, ActionIcon } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { HiMagnifyingGlass, HiXMark } from "react-icons/hi2";
import CustomTextInput from "../CustomTextInput/CustomTextInput";
import CustomBadge from "../CustomBadge/CustomBadge";

const CustomSearchBar = ({
  placeholder = "Search...",
  onSearchChange,
  value = "",
  badgeCount,
  badgeLabel = "items",
  debounceDelay = 300,
  style,
  className,
  inputStyle,
  size,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [debounced] = useDebouncedValue(inputValue, debounceDelay);

  const lastSentRef = React.useRef(value);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  React.useEffect(() => {
    if (!onSearchChange) return;
    if (debounced === lastSentRef.current) return;
    lastSentRef.current = debounced;
    onSearchChange(debounced);
  }, [debounced, onSearchChange]);

  const handleClear = () => setInputValue("");

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      style={style}
      className={className}
    >
      <CustomTextInput
        leftSection={<HiMagnifyingGlass size={16} />}
        rightSection={
          inputValue ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={handleClear}
              aria-label="Clear search"
            >
              <HiXMark size={14} />
            </ActionIcon>
          ) : null
        }
        value={inputValue}
        onChange={(e) => setInputValue(e.currentTarget.value)}
        size={size}
        style={{ flex: 1, maxWidth: "none", ...inputStyle }}
        placeholder={placeholder}
        disabled={disabled}
      />
      {badgeCount !== undefined && (
        <div className="hidden sm:block">
          <CustomBadge variant="light" color="primary" size="sm">
            {badgeCount} {badgeLabel}
          </CustomBadge>
        </div>
      )}
    </Group>
  );
};

CustomSearchBar.propTypes = {
  placeholder: PropTypes.string,
  onSearchChange: PropTypes.func,
  value: PropTypes.string,
  badgeCount: PropTypes.number,
  badgeLabel: PropTypes.string,
  debounceDelay: PropTypes.number,
  style: PropTypes.object,
  className: PropTypes.string,
  inputStyle: PropTypes.object,
  size: PropTypes.string,
  disabled: PropTypes.bool,
};

export default CustomSearchBar;
